import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Upload, Printer, X } from 'lucide-react';
import { get, download, upload } from '../api/client';
import { useAuth } from '../hooks/useAuth';

/**
 * Financials: the church's operating ledger as a statement of activities, trends,
 * costs and budget vs actual. Every number comes from the API's /financials/report,
 * which computes them all with one formula module (app/fincalc.py), and the CSV
 * export uses the same loader, so cards, charts, tables and the file always agree.
 * This page formats; it never adds up money itself.
 */

const TABS = [
  ['overview', 'Overview'],
  ['pnl', 'Statement of activities'],
  ['trends', 'Giving trends'],
  ['costs', 'Expenses'],
  ['budget', 'Budget vs actual'],
  ['data', 'Data'],
];

// ---------- formatting (display only; integer cents in) ----------
const money = (c) => {
  const neg = c < 0;
  const a = Math.abs(c);
  return `${neg ? '-' : ''}$${Math.floor(a / 100).toLocaleString('en-US')}.${String(a % 100).padStart(2, '0')}`;
};
const compact = (c) => {
  const neg = c < 0;
  const d = Math.round(Math.abs(c) / 100);
  const s = d >= 1_000_000 ? `$${Math.floor(Math.round(d / 100_000) / 10)}.${Math.round(d / 100_000) % 10}M` : d >= 1_000 ? `$${Math.round(d / 1_000)}k` : `$${d}`;
  return `${neg ? '-' : ''}${s}`;
};
const bpsText = (b) => `${Math.floor(Math.abs(b) / 100)}.${String(Math.abs(b) % 100).padStart(2, '0')}%`;
const pct = (b) => (b === null || b === undefined ? 'N/A' : `${b < 0 ? '-' : ''}${bpsText(b)}`);
const signedPct = (b) => (b === null || b === undefined ? null : `${b < 0 ? '-' : '+'}${bpsText(b)}`);
const pp = (b) => (b === null || b === undefined ? null : `${b < 0 ? '-' : '+'}${bpsText(b).replace('%', '')} pp`);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const mon = (ym) => MONTHS[Number(ym.slice(5, 7)) - 1];
const monYear = (ym) => `${mon(ym)} ${ym.slice(0, 4)}`;
const longDate = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });

// ---------- visuals ----------
function Panel({ title, aside, children, className = '' }) {
  return (
    <section className={`fin-card rounded-xl p-4 ${className}`}>
      {(title || aside) && (
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          {title && <h3 className="text-sm font-medium text-white/90">{title}</h3>}
          {aside && <div className="text-xs text-white/55">{aside}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

function Delta({ text, good }) {
  if (text === null) return <span className="text-[11px] text-white/50">N/A</span>;
  const tone = good === null ? 'text-white/60' : good ? 'text-emerald-300' : 'text-rose-300';
  const arrow = text.startsWith('-') ? '▼' : '▲';
  return <span className={`text-[11px] font-medium ${tone}`}>{arrow} {text.replace(/^[+-]/, '')}</span>;
}

function Ring({ bps, size = 120, label, sub }) {
  const segs = 28;
  const lit = bps === null || bps === undefined ? 0 : Math.round((Math.max(0, Math.min(10_000, bps)) / 10_000) * segs);
  // Spoke length and width scale with the dial, so a 52px card ring stays a ring, not a disc.
  const r = size / 2 - Math.max(3, size * 0.06), spoke = size * 0.07, sw = Math.max(2, size * 0.045);
  return (
    <figure className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${sub || ''} ${label}`}>
        {Array.from({ length: segs }, (_, i) => {
          const a = (i / segs) * Math.PI * 2 - Math.PI / 2;
          return (
            <line key={i} x1={size / 2 + Math.cos(a) * (r - spoke)} y1={size / 2 + Math.sin(a) * (r - spoke)} x2={size / 2 + Math.cos(a) * r} y2={size / 2 + Math.sin(a) * r}
              strokeWidth={sw} strokeLinecap="round" className={i < lit ? 'fin-ring-on' : 'fin-ring-off'} style={{ animationDelay: `${i * 18}ms` }} />
          );
        })}
        <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="fill-white font-semibold" style={{ fontSize: Math.max(9, Math.round(size / (size < 80 ? 4.6 : 7))) }}>{label}</text>
      </svg>
      {sub && <figcaption className="mt-1 text-center text-[11px] text-white/60">{sub}</figcaption>}
    </figure>
  );
}

function Kpi({ label, value, delta, good, sub, ring, ringLabel, onClick, glow }) {
  return (
    <button type="button" onClick={onClick} data-kpi={label} className={`fin-card fin-glow-${glow} block w-full rounded-xl p-4 text-left transition hover:-translate-y-0.5`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-white/70">{label}</p>
          <p className="fin-num mt-1 text-xl font-semibold text-white">{value}</p>
          <p className="mt-1"><Delta text={delta} good={good} /> <span className="text-[11px] text-white/45">{sub}</span></p>
        </div>
        {ring !== undefined && <Ring bps={ring} size={52} label={ringLabel} />}
      </div>
    </button>
  );
}

/** Rounded bars. null = gap (no records), drawn as a dashed stub, never zero. */
function Bars({ points, height = 210 }) {
  const vals = points.map((p) => p.value ?? 0);
  const max = Math.max(1, ...vals);
  const min = Math.min(0, ...vals);
  const span = max - min;
  const w = 640, top = 34, bottom = 22, left = 44, h = height;
  const y = (v) => top + ((max - v) / span) * (h - top - bottom);
  const step = (w - left) / Math.max(points.length, 1);
  const bw = Math.min(30, step * 0.55);
  const hi = [...points].reverse().find((p) => p.value !== null && !p.incomplete);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-auto w-full" role="img" aria-label="Monthly chart">
      <defs>
        <linearGradient id="fin-bar" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#a5b4fc" /><stop offset="100%" stopColor="#4338ca" /></linearGradient>
        <linearGradient id="fin-bar-hi" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#f0abfc" /><stop offset="100%" stopColor="#a855f7" stopOpacity="0.6" /></linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((f) => { const v = min + span * f; return <g key={f}><line x1={left} x2={w} y1={y(v)} y2={y(v)} stroke="rgba(255,255,255,0.1)" /><text x={left - 6} y={y(v) + 3} textAnchor="end" className="fill-white/40" style={{ fontSize: 9 }}>{compact(Math.round(v))}</text></g>; })}
      {points.map((p, i) => {
        const cx = left + step * i + step / 2;
        if (p.value === null) return <g key={p.key}><title>{`${p.tip}: no records`}</title><line x1={cx} x2={cx} y1={y(0) - 14} y2={y(0)} stroke="rgba(255,255,255,0.35)" strokeDasharray="2 3" strokeWidth={2} /><text x={cx} y={h - 6} textAnchor="middle" className="fill-white/50" style={{ fontSize: 10 }}>{p.label}</text></g>;
        const y0 = y(Math.max(0, p.value)), y1 = y(Math.min(0, p.value));
        const isHi = hi && hi.key === p.key;
        return (
          <g key={p.key} data-bar={p.key}>
            <title>{`${p.tip}: ${money(p.value)}${p.incomplete ? ' (month in progress)' : ''}`}</title>
            <rect x={cx - bw / 2} y={y0} width={bw} height={Math.max(2, y1 - y0)} rx={bw / 2} fill={isHi ? 'url(#fin-bar-hi)' : 'url(#fin-bar)'} opacity={p.incomplete ? 0.45 : 1} className="fin-bar" style={{ animationDelay: `${i * 35}ms` }} />
            {p.incomplete && <rect x={cx - bw / 2} y={y0} width={bw} height={Math.max(2, y1 - y0)} rx={bw / 2} fill="none" stroke="rgba(255,255,255,0.6)" strokeDasharray="3 3" />}
            {isHi && <><circle cx={cx} cy={y0 + 7} r={3.5} fill="#fff" /><rect x={cx - 44} y={Math.max(2, y0 - 30)} width={88} height={20} rx={10} fill="#1e1b4b" stroke="rgba(255,255,255,0.3)" /><text x={cx} y={Math.max(2, y0 - 30) + 14} textAnchor="middle" className="fill-white font-semibold" style={{ fontSize: 10 }}>{compact(p.value)}</text></>}
            <text x={cx} y={h - 6} textAnchor="middle" className="fill-white/60" style={{ fontSize: 10 }}>{p.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function HBars({ rows, onPick }) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.amount)));
  const hasNeg = rows.some((r) => r.amount < 0);
  return (
    <ul className="space-y-2">
      {rows.map((r) => {
        const w = (Math.abs(r.amount) / max) * (hasNeg ? 50 : 100);
        const style = hasNeg ? (r.amount < 0 ? { right: '50%', width: `${w}%` } : { left: '50%', width: `${w}%` }) : { left: 0, width: `${w}%` };
        return (
          <li key={`${r.category}:${r.account}`}>
            <button type="button" onClick={() => onPick(r.account)} className="block w-full rounded-md p-1 text-left hover:bg-white/5" data-cost-account={r.account}>
              <div className="flex justify-between gap-2 text-xs"><span className="truncate text-white/85">{r.account}</span><span className="fin-num text-white/70">{money(r.amount)} · {pct(r.share_bps)}</span></div>
              <div className="relative mt-1 h-2 rounded-full bg-white/10">
                {hasNeg && <span className="absolute -inset-y-1 left-1/2 w-px bg-white/60" />}
                <span className={`fin-hbar absolute inset-y-0 rounded-full ${r.amount < 0 ? 'bg-rose-400' : 'bg-gradient-to-r from-indigo-700 to-fuchsia-400'}`} style={style} />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

const STATUS = {
  FAVORABLE: ['bg-emerald-400/15 text-emerald-200 ring-emerald-400/40', '▲', 'Favorable'],
  UNFAVORABLE: ['bg-rose-400/15 text-rose-200 ring-rose-400/40', '▼', 'Unfavorable'],
  ON_BUDGET: ['bg-white/10 text-white/80 ring-white/25', '•', 'On budget'],
  NO_BUDGET: ['bg-amber-400/15 text-amber-200 ring-amber-400/40', '–', 'No budget'],
};

// ---------- page ----------
export default function Financials() {
  const { role } = useAuth();
  const canUpload = role === 'admin' || role === 'pastor';
  const [tab, setTab] = useState('overview');
  const [range, setRange] = useState({ from: '', to: '' });
  const [picked, setPicked] = useState([]);
  const [applied, setApplied] = useState({ from: '', to: '', funds: [] });
  const [r, setR] = useState(null);
  const [error, setError] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [account, setAccount] = useState('');
  const [uploadMsg, setUploadMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const query = useMemo(() => {
    const sp = new URLSearchParams();
    if (applied.from) sp.set('from', applied.from);
    if (applied.to) sp.set('to', applied.to);
    if (applied.funds.length) sp.set('funds', applied.funds.join(','));
    const s = sp.toString();
    return s ? `?${s}` : '';
  }, [applied]);

  const load = useCallback(async () => {
    try {
      setError('');
      const data = await get(`/api/v1/financials/report${query}`);
      setR(data);
      setRange((x) => (x.from ? x : { from: data.from, to: data.to }));
    } catch (e) {
      setError(e.message);
    }
  }, [query]);
  useEffect(() => { load(); }, [load]);

  const apply = (e) => { e.preventDefault(); setApplied({ from: range.from, to: range.to, funds: picked }); };
  const openRecords = (cat) => { setCatFilter(cat); setAccount(''); setTab('data'); };

  const onUpload = async (e) => {
    e.preventDefault();
    const file = e.target.elements.file.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const res = await upload('/api/v1/financials/upload', fd);
      setUploadMsg({ ok: true, res });
      e.target.reset();
      await load();
    } catch (err) {
      setUploadMsg({ ok: false, detail: err.detail, message: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-200">{error}</div>;
  if (!r) return <p className="text-slate-500">Loading financials…</p>;

  const s = r.statement;
  const label = r.category_label;
  const fundsText = r.picked.length ? r.picked.map((id) => r.names[id]).join(', ') : 'all funds';

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Financials</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
          From the church's operating ledger. Every card, chart, table and export is computed by the same formulas, so their totals always agree.
        </p>
      </div>

      <div className="fin-board rounded-2xl p-4 sm:p-5">
        <div className="fin-noprint flex flex-wrap items-center justify-between gap-3">
          <nav className="flex flex-wrap gap-1 rounded-full bg-white/5 p-1" aria-label="Financials views">
            {TABS.map(([k, name]) => (
              <button key={k} type="button" onClick={() => setTab(k)} aria-current={tab === k ? 'page' : undefined} className="fin-tab rounded-full px-3 py-1 text-xs font-medium">{name}</button>
            ))}
          </nav>
          <button type="button" data-export onClick={() => download(`/api/v1/financials/export${query}`, `ledger-${r.from}-to-${r.to}.csv`).catch((e) => setError(e.message))}
            className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white ring-1 ring-white/20 hover:bg-white/15">
            <Download size={14} /> Export filtered records (CSV)
          </button>
        </div>

        <form onSubmit={apply} className="fin-noprint mt-4 flex flex-wrap items-end gap-3 text-xs text-white/75">
          <label>From<input type="month" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="mt-1 block rounded-md border border-white/20 bg-white/5 px-2 py-1 text-sm text-white" /></label>
          <label>Through<input type="month" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="mt-1 block rounded-md border border-white/20 bg-white/5 px-2 py-1 text-sm text-white" /></label>
          <fieldset className="flex flex-wrap gap-2"><legend className="mb-1">Funds</legend>
            {r.funds.map((f) => (
              <label key={f.id} className="flex items-center gap-1 rounded-full bg-white/5 px-2 py-1">
                <input type="checkbox" checked={picked.includes(f.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, f.id] : picked.filter((x) => x !== f.id))} />
                {f.name}{f.is_restricted && <span className="text-white/45">(restricted)</span>}
              </label>
            ))}
          </fieldset>
          <button className="rounded-full bg-fuchsia-400 px-4 py-1.5 text-xs font-semibold text-indigo-950">Apply</button>
        </form>
        <p className="mt-3 text-xs text-white/55" data-active-filters>
          Showing {monYear(r.from)} through {monYear(r.to)} · {fundsText} · as of {longDate(r.as_of)} · USD · sample data
        </p>

        <div className="mt-4">
          {tab === 'overview' && <Overview r={r} s={s} openRecords={openRecords} />}
          {tab === 'pnl' && <Activities r={r} s={s} label={label} fundsText={fundsText} />}
          {tab === 'trends' && <Trends r={r} />}
          {tab === 'costs' && <Costs r={r} account={account} setAccount={setAccount} />}
          {tab === 'budget' && <Budget r={r} label={label} />}
          {tab === 'data' && <Data r={r} label={label} catFilter={catFilter} setCatFilter={setCatFilter} canUpload={canUpload} onUpload={onUpload} uploadMsg={uploadMsg} busy={busy} />}
        </div>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">Operating figures are the church's own books, shown for record-keeping and stewardship. They are not a forecast or a pledge.</p>
    </div>
  );
}

function Overview({ r, s, openRecords }) {
  const vs = `vs ${mon(r.prior.from)}–${mon(r.prior.to)}`;
  const k = r.kpi;
  const costs = r.operating_costs;
  const ratio = r.expense_ratio_bps;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi glow="indigo" label="Contributions" value={money(s.net_revenue)} delta={signedPct(k.net_revenue)} good={k.net_revenue === null ? null : k.net_revenue >= 0} sub={vs} onClick={() => openRecords('net_revenue')} />
        <Kpi glow="emerald" label="After ministry programs" value={money(s.gross_profit)} delta={signedPct(k.gross_profit)} good={k.gross_profit === null ? null : k.gross_profit >= 0} sub={vs} ring={s.gross_margin_bps} ringLabel={s.gross_margin_bps === null ? 'N/A' : `${Math.round(s.gross_margin_bps / 100)}%`} onClick={() => openRecords('cogs')} />
        <Kpi glow="fuchsia" label="Operating surplus" value={money(s.noi)} delta={signedPct(k.noi)} good={k.noi === null ? null : k.noi >= 0} sub={vs} onClick={() => openRecords('opex')} />
        <Kpi glow="amber" label="Surplus margin" value={pct(s.noi_margin_bps)} delta={pp(k.noi_margin_pp)} good={k.noi_margin_pp === null ? null : k.noi_margin_pp >= 0} sub={vs} ring={s.noi_margin_bps} ringLabel={s.noi_margin_bps === null ? 'N/A' : `${Math.round(s.noi_margin_bps / 100)}%`} onClick={() => openRecords('net_revenue')} />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel className="lg:col-span-2" title="Monthly giving" aside={<span className="fin-num text-base font-semibold text-white">{money(s.net_revenue)}</span>}>
          <Bars points={r.series.map((x) => ({ key: x.month, label: mon(x.month), value: x.revenue, incomplete: !x.complete, tip: monYear(x.month) }))} />
          <p className="mt-1 text-[11px] text-white/50">Faded bar: the current month, still in progress, left out of growth figures. A dashed stub means no records, not zero.</p>
        </Panel>
        <Panel title="Expense ratio" aside="Programs + administration ÷ giving">
          <Ring bps={ratio} size={150} label={pct(ratio)} sub={`${compact(costs)} of ${compact(s.net_revenue)}`} />
          <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div><dt className="text-white/55">Records</dt><dd className="fin-num text-white">{s.entry_count.toLocaleString()}</dd></div>
            <div><dt className="text-white/55">Change in net assets</dt><dd className="fin-num text-white">{money(s.net_income)}</dd></div>
          </dl>
        </Panel>
      </div>
      <Panel title="By fund" aside="The total margin is computed from the totals, not by averaging the funds' margins.">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-white/85">
            <thead className="text-left text-xs text-white/55"><tr><th className="py-1">Fund</th><th className="text-right">Contributions</th><th className="text-right">Operating surplus</th><th className="text-right">Margin</th></tr></thead>
            <tbody className="divide-y divide-white/10">
              {r.by_fund.map(({ fund_id: id, statement: fs }) => (
                <tr key={id}><td className="py-1.5">{r.names[id]}</td><td className="fin-num text-right">{money(fs.net_revenue)}</td><td className="fin-num text-right">{money(fs.noi)}</td><td className="fin-num text-right">{pct(fs.noi_margin_bps)}</td></tr>
              ))}
              <tr className="font-semibold text-white" data-portfolio-margin={r.portfolio_margin_bps}><td className="py-1.5">All funds</td><td className="fin-num text-right">{money(s.net_revenue)}</td><td className="fin-num text-right">{money(s.noi)}</td><td className="fin-num text-right">{pct(r.portfolio_margin_bps)}</td></tr>
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function Activities({ r, s, label, fundsText }) {
  const y = r.ytd.statement;
  const yd = r.ytd.detail;
  const Cat = ({ c, sign }) => (
    <tbody className="border-t border-white/10">
      <tr className="text-white/90">
        <td className="py-1.5 pr-3">
          <details data-pnl-category={c}>
            <summary className="cursor-pointer">{sign} {label[c]} <span className="text-[11px] text-white/45">({r.detail[c].length} accounts)</span></summary>
            <ul className="mt-1 space-y-0.5 pl-4 text-xs text-white/65">
              {r.detail[c].map((a) => <li key={a.account} className="flex justify-between gap-4"><span>{a.account}</span><span className="fin-num">{money(a.amount)} · YTD {money((yd[c].find((x) => x.account === a.account) || { amount: 0 }).amount)}</span></li>)}
            </ul>
          </details>
        </td>
        <td className="fin-num pt-1.5 text-right align-top">{money(s[c])}</td><td className="fin-num pt-1.5 text-right align-top">{money(y[c])}</td>
      </tr>
    </tbody>
  );
  const Line = ({ name, a, b, cls = '' }) => <tbody><tr className={`font-semibold text-white ${cls}`}><td className="py-1.5 pr-3">{name}</td><td className="fin-num text-right">{money(a)}</td><td className="fin-num text-right">{money(b)}</td></tr></tbody>;
  const rows = [['Contributions', 'net_revenue'], ['Ministry programs', 'cogs'], ['After programs', 'gross_profit'], ['Administration and facilities', 'opex'], ['Operating surplus', 'noi'], ['Change in net assets', 'net_income']];
  return (
    <div className="space-y-4">
      <Panel title="Statement of activities" aside={<button type="button" onClick={() => window.print()} className="fin-noprint flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-white"><Printer size={12} /> Print</button>}>
        <p className="mb-2 text-xs text-white/55">Church Workflow demo congregation (sample data) · {fundsText} · {longDate(r.from_date)} – {longDate(r.to_date)} · USD · accrual basis</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-pnl>
            <thead className="text-left text-xs text-white/55"><tr><th className="py-1">Click a category for its accounts</th><th className="text-right">Selected period</th><th className="text-right">Year to date ({mon(r.ytd.from)} – {mon(r.to)})</th></tr></thead>
            <Cat c="net_revenue" sign="+" />
            <Cat c="cogs" sign="−" />
            <Line name="After ministry programs" a={s.gross_profit} b={y.gross_profit} />
            <Cat c="opex" sign="−" />
            <Line name="Operating surplus (deficit)" a={s.noi} b={y.noi} cls="border-t border-white/20" />
            <Cat c="other_income" sign="+" /><Cat c="other_expense" sign="−" /><Cat c="interest_expense" sign="−" /><Cat c="income_tax" sign="−" />
            <Line name="Change in net assets" a={s.net_income} b={y.net_income} cls="border-t-2 border-white/30" />
            <tbody className="text-xs text-white/60"><tr><td className="pt-2">Surplus margin</td><td className="fin-num pt-2 text-right">{pct(s.noi_margin_bps)}</td><td className="fin-num pt-2 text-right">{pct(y.noi_margin_bps)}</td></tr></tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-white/50">Depreciation is not in the operating ledger, so nothing is deducted twice. Margins are from period totals.</p>
      </Panel>
      <Panel title="Month by month">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-white/80">
            <thead className="text-white/55"><tr><th className="py-1 text-left">Line</th>{r.monthly.map((m) => <th key={m.month} className="px-2 text-right">{mon(m.month)}</th>)}<th className="px-2 text-right">Total</th></tr></thead>
            <tbody className="divide-y divide-white/10">
              {rows.map(([name, k]) => (
                <tr key={k} className={['gross_profit', 'noi', 'net_income'].includes(k) ? 'font-semibold text-white' : ''}><td className="py-1 pr-2">{name}</td>{r.monthly.map((m) => <td key={m.month} className="fin-num px-2 text-right">{compact(m.statement[k])}</td>)}<td className="fin-num px-2 text-right">{compact(s[k])}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function Trends({ r }) {
  const ch = (b, c) => (b === null ? <span className="text-white/50">N/A{c !== null ? ` (${money(c)})` : ''}</span> : <span className={b >= 0 ? 'text-emerald-300' : 'text-rose-300'}>{b >= 0 ? '▲' : '▼'} {signedPct(b)} <span className="text-white/50">({c >= 0 ? '+' : ''}{money(c)})</span></span>);
  return (
    <div className="space-y-4">
      <Panel title="Monthly giving" aside="Growth compares completed calendar months only">
        <Bars points={r.series.map((x) => ({ key: x.month, label: mon(x.month), value: x.revenue, incomplete: !x.complete, tip: `${monYear(x.month)}${x.mom_bps !== null ? ` · MoM ${signedPct(x.mom_bps)}` : ''}${x.yoy_bps !== null ? ` · YoY ${signedPct(x.yoy_bps)}` : ''}` }))} />
      </Panel>
      <Panel>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-white/85" data-trends>
            <thead className="text-left text-xs text-white/55"><tr><th className="py-1">Month</th><th className="text-right">Contributions</th><th className="pl-4">Month over month</th><th>Year over year</th></tr></thead>
            <tbody className="divide-y divide-white/10">
              {[...r.series].reverse().map((x) => (
                <tr key={x.month} data-month={x.month}>
                  <td className="py-1.5">{monYear(x.month)}{!x.complete && <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[10px]">In progress</span>}</td>
                  <td className="fin-num text-right">{x.revenue === null ? <span className="text-white/50">No records</span> : money(x.revenue)}</td>
                  <td className="pl-4 text-xs">{ch(x.mom_bps, x.mom_change)}</td><td className="text-xs">{ch(x.yoy_bps, x.yoy_change)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-white/50">N/A where the comparison month is missing, in progress, or zero or negative. The dollar change is shown whenever both months exist.</p>
      </Panel>
    </div>
  );
}

function Costs({ r, account, setAccount }) {
  const c = r.costs;
  const accounts = [...new Set(c.accounts.map((a) => a.account))];
  const depts = Object.keys(c.by_department);
  const drill = account ? r.records.filter((e) => (e.category === 'cogs' || e.category === 'opex') && e.account === account) : [];
  const byName = (a) => c.accounts.find((x) => x.account === a);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Expenses" aside="Accounting costs: ministry programs + administration">
          <p className="fin-num text-2xl font-semibold text-white" data-cost-total>{money(c.total)}</p>
          <p className="mt-1 text-xs text-white/55">{c.accounts.length} accounts · {c.accounts.reduce((a, x) => a + x.count, 0)} records</p>
        </Panel>
        <Panel className="lg:col-span-2" title="Five largest accounts"><HBars rows={c.accounts.slice(0, 5)} onPick={setAccount} /></Panel>
      </div>
      {account && (
        <Panel title={`Transactions: ${account}`} aside={<button type="button" onClick={() => setAccount('')} className="flex items-center gap-1 underline"><X size={12} /> Close</button>}>
          <div className="overflow-x-auto"><table className="w-full text-sm text-white/85" data-drilldown>
            <thead className="text-left text-xs text-white/55"><tr><th className="py-1">Date</th><th>Record</th><th>Fund</th><th className="text-right">Amount</th></tr></thead>
            <tbody className="divide-y divide-white/10">
              {drill.map((e) => <tr key={e.record_id}><td className="py-1">{longDate(e.date)}</td><td className="font-mono text-xs">{e.record_id}</td><td>{r.names[e.fund_id]}</td><td className={`fin-num text-right ${e.amount < 0 ? 'text-rose-300' : ''}`}>{money(e.amount)}</td></tr>)}
              <tr className="font-semibold text-white"><td className="py-1" colSpan={3}>Total</td><td className="fin-num text-right">{money(byName(account).amount)}</td></tr>
            </tbody>
          </table></div>
        </Panel>
      )}
      <Panel title="Fund by account">
        <div className="overflow-x-auto"><table className="w-full text-xs text-white/80">
          <thead className="text-white/55"><tr><th className="py-1 text-left">Account</th>{depts.map((d) => <th key={d} className="px-2 text-right">{r.names[d]}</th>)}<th className="px-2 text-right">Share</th></tr></thead>
          <tbody className="divide-y divide-white/10">
            {accounts.map((a) => <tr key={a}><td className="py-1"><button type="button" onClick={() => setAccount(a)} className="underline decoration-white/30">{a}</button></td>{depts.map((d) => <td key={d} className="fin-num px-2 text-right">{c.by_department[d][a] === undefined ? '—' : money(c.by_department[d][a])}</td>)}<td className="fin-num px-2 text-right">{pct(byName(a).share_bps)}</td></tr>)}
          </tbody>
        </table></div>
      </Panel>
    </div>
  );
}

function Budget({ r, label }) {
  return (
    <Panel title="Budget vs actual" aside="Variance = actual − budget. Higher giving is favorable; higher cost is unfavorable.">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-white/85" data-budget>
          <thead className="text-left text-xs text-white/55"><tr><th className="py-1">Fund</th><th>Category</th><th className="text-right">Actual</th><th className="text-right">Budget</th><th className="text-right">Variance</th><th className="text-right">%</th><th className="pl-3">Status</th></tr></thead>
          <tbody className="divide-y divide-white/10">
            {r.budget.map((b) => {
              const [cls, mark, word] = STATUS[b.status];
              return (
                <tr key={`${b.department}|${b.category}`} data-variance={`${r.names[b.department]}|${b.category}`}>
                  <td className="py-1.5">{r.names[b.department]}</td><td>{label[b.category]}</td>
                  <td className="fin-num text-right">{money(b.actual)}</td>
                  <td className="fin-num text-right">{b.budget === null ? <span className="text-white/50">None set</span> : money(b.budget)}</td>
                  <td className="fin-num text-right">{b.variance === null ? '—' : `${b.variance > 0 ? '+' : ''}${money(b.variance)}`}</td>
                  <td className="fin-num text-right">{b.variance_bps === null ? 'N/A' : signedPct(b.variance_bps)}</td>
                  <td className="pl-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}>{mark} {word}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-white/50">"None set" means no budget was entered, which is different from a budget of $0. A $0 budget shows the variance in dollars with N/A for the percentage.</p>
    </Panel>
  );
}

function Data({ r, label, catFilter, setCatFilter, canUpload, onUpload, uploadMsg, busy }) {
  const rows = r.records.filter((e) => !catFilter || e.category === catFilter);
  return (
    <div className="space-y-4">
      {uploadMsg && uploadMsg.ok && (
        <div className="rounded-lg bg-emerald-400/15 p-3 text-sm text-emerald-100 ring-1 ring-emerald-400/40" data-upload-ok>
          {uploadMsg.res.rows} records added. Read back from the database: {uploadMsg.res.rows} rows totalling {money(uploadMsg.res.net_amount)}.
        </div>
      )}
      {uploadMsg && !uploadMsg.ok && (
        <div className="rounded-lg bg-rose-400/15 p-3 text-sm text-rose-100 ring-1 ring-rose-400/40" data-upload-refused>
          <p className="font-semibold">{uploadMsg.detail?.message || uploadMsg.message}</p>
          {uploadMsg.detail?.problems && <>
            <p>{uploadMsg.detail.count} problems. Fix them and upload the whole file again.</p>
            <ul className="mt-1 list-disc pl-5">{uploadMsg.detail.problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </>}
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Upload ledger (CSV)" className="fin-noprint">
          {canUpload ? (
            <form onSubmit={onUpload} className="space-y-3 text-xs text-white/75">
              <input type="file" name="file" accept=".csv,text/csv" required className="block w-full text-white file:mr-3 file:rounded-full file:border-0 file:bg-white/15 file:px-3 file:py-1 file:text-white" />
              <button disabled={busy} className="flex items-center gap-1 rounded-full bg-fuchsia-400 px-4 py-1.5 font-semibold text-indigo-950 disabled:opacity-50"><Upload size={12} /> {busy ? 'Uploading…' : 'Upload'}</button>
            </form>
          ) : <p className="text-xs text-white/60">Only the pastor or an administrator can upload the books. The demo account is read-only.</p>}
          <p className="mt-3 text-[11px] text-white/55">Columns: record_id, date (YYYY-MM-DD), department (fund name), account, category, amount, currency. Normal amounts positive, reversals negative. One currency per file. Any bad row refuses the whole file.</p>
          <button type="button" onClick={() => download('/api/v1/financials/template', 'ledger-template.csv')} className="mt-2 text-xs text-white underline">Download the CSV template</button>
        </Panel>
        <Panel className="lg:col-span-2" title={catFilter ? `Records: ${label[catFilter]}` : 'Records'} aside={<span className="fin-num">{rows.length} records{catFilter ? ` · ${money(r.statement[catFilter])}` : ''}</span>}>
          <div className="mb-2 flex flex-wrap gap-1 text-[11px]">
            <button type="button" onClick={() => setCatFilter('')} className={`rounded-full px-2 py-0.5 ${!catFilter ? 'bg-white/20 text-white' : 'bg-white/5 text-white/70'}`}>All</button>
            {Object.keys(label).map((c) => <button key={c} type="button" onClick={() => setCatFilter(c)} className={`rounded-full px-2 py-0.5 ${catFilter === c ? 'bg-white/20 text-white' : 'bg-white/5 text-white/70'}`}>{label[c]}</button>)}
          </div>
          <div className="max-h-[28rem] overflow-auto">
            <table className="w-full text-xs text-white/80" data-records>
              <thead className="sticky top-0 bg-indigo-950 text-left text-white/55"><tr><th className="py-1">Date</th><th>Record</th><th>Fund</th><th>Account</th><th className="text-right">Amount</th></tr></thead>
              <tbody className="divide-y divide-white/10">
                {rows.slice(0, 400).map((e) => <tr key={e.record_id}><td className="py-1">{longDate(e.date)}</td><td className="font-mono">{e.record_id}</td><td>{r.names[e.fund_id]}</td><td>{e.account}</td><td className={`fin-num text-right ${e.amount < 0 ? 'text-rose-300' : ''}`}>{money(e.amount)}</td></tr>)}
              </tbody>
            </table>
            {rows.length > 400 && <p className="mt-2 text-[11px] text-white/55">Showing the first 400. The export has all {rows.length}.</p>}
          </div>
        </Panel>
      </div>
    </div>
  );
}
